import {
  getMigrationLatestRuns,
  inspectCollection,
  startMigration
} from "@domain/migrations"
import useTranslate from "@hooks/use_translate"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography
} from "@mui/material"
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  showErrorToast,
  showSuccessToast,
  showWarningToast
} from "@utils/toast"
import { useEffect, useRef, useState } from "react"
import ActiveRunBanner from "./active_run_banner"
import MigrationDetailDrawer from "./detail_drawer"
import {
  InspectCollectionInput,
  InspectCollectionResult,
  MigrationRow,
  MigrationRun,
  MigrationSpec,
  MigrationState,
  isSeedSpec,
  pickText,
  runCount,
  sectionOf
} from "./migration.models"
import { probeMigration, progressCount } from "./migration.probe"
import MigrationTable from "./migration_table"
import MIGRATION_REGISTRY, { ARCHIVED_MIGRATIONS } from "./registry"
import RunConfirmDialog from "./run_confirm_dialog"
import useNow from "./use_now"

const RUN_POLL_MS = 5000
const RUNS_KEY = ["migration-latest-runs"]
const probeKey = (type: string) => ["migration-probe", type]

/** Unwraps the API envelope; the probe engine only ever sees a result or an exception. */
async function fetchProbe(
  input: InspectCollectionInput
): Promise<InspectCollectionResult> {
  const response = await inspectCollection(input)
  const result = response.data?.data
  if (!result) {
    throw new Error(response.data?.message ?? "probe returned no data")
  }
  return result
}

async function fetchLatestRuns(): Promise<MigrationRun[]> {
  const response = await getMigrationLatestRuns()
  const runs: MigrationRun[] = response.data?.data ?? []
  return runs
}

/**
 * One query per spec, so a slow collection on production holds up its own row and nothing else.
 * Probes never refresh on their own — each is a count against a live database.
 */
const probeQuery = (spec: MigrationSpec, enabled: boolean) => ({
  queryKey: probeKey(spec.type),
  queryFn: () => probeMigration(spec, fetchProbe),
  enabled,
  staleTime: Infinity
})

const SPEC_BY_TYPE = new Map(
  [...MIGRATION_REGISTRY, ...ARCHIVED_MIGRATIONS].map((spec) => [
    spec.type,
    spec
  ])
)

export default function MigrationsPage() {
  const { translate, currentLanguage } = useTranslate()
  const queryClient = useQueryClient()
  const [selectedType, setSelectedType] = useState<string | null>(null)
  const [confirming, setConfirming] = useState<MigrationState | null>(null)
  const [starting, setStarting] = useState(false)
  const [showApplied, setShowApplied] = useState(false)
  const [showArchived, setShowArchived] = useState(false)

  const runsQuery = useQuery({
    queryKey: RUNS_KEY,
    queryFn: fetchLatestRuns,
    refetchInterval: (query) =>
      query.state.data?.some((run) => run.status === "RUNNING")
        ? RUN_POLL_MS
        : false
  })
  const activeProbes = useQueries({
    queries: MIGRATION_REGISTRY.map((spec) => probeQuery(spec, true))
  })
  const archivedProbes = useQueries({
    queries: ARCHIVED_MIGRATIONS.map((spec) => probeQuery(spec, showArchived))
  })

  const latestRuns = runsQuery.data ?? []
  const toRows = (
    specs: MigrationSpec[],
    probes: typeof activeProbes,
    archived: boolean
  ): MigrationRow[] =>
    specs.map((spec, index) => ({
      spec,
      order: index + 1,
      archived,
      state: probes[index]?.data,
      probing: probes[index]?.isFetching ?? false,
      lastRun: latestRuns.find((run) => run.type === spec.type)
    }))
  const activeRows = toRows(MIGRATION_REGISTRY, activeProbes, false)
  const archivedRows = toRows(ARCHIVED_MIGRATIONS, archivedProbes, true)
  const allRows = [...activeRows, ...archivedRows]

  const runningRows = allRows.filter((row) => sectionOf(row) === "RUNNING")
  const attentionRows = activeRows.filter(
    (row) => sectionOf(row) === "ATTENTION"
  )
  const appliedRows = activeRows.filter((row) => sectionOf(row) === "APPLIED")
  const checkingCount = activeRows.filter((row) => !row.state).length
  const failedCount = activeRows.filter((row) => !!row.state?.probeError).length
  const probing = [...activeProbes, ...archivedProbes].some(
    (probe) => probe.isFetching
  )

  const now = useNow(runningRows.length > 0)
  const selected = allRows.find((row) => row.spec.type === selectedType)

  const previouslyRunning = useRef<Set<string>>(new Set())
  useEffect(() => {
    if (!runsQuery.data) return
    const running = new Set(
      runsQuery.data
        .filter((run) => run.status === "RUNNING")
        .map((run) => run.type)
    )
    previouslyRunning.current.forEach((type) => {
      if (running.has(type)) return
      const run = runsQuery.data.find((candidate) => candidate.type === type)
      const spec = SPEC_BY_TYPE.get(type)
      if (run && spec) {
        const title = pickText(spec.title, currentLanguage)
        const measured = progressCount(spec).key
        if (run.status === "SUCCEEDED") {
          showSuccessToast(
            translate(
              isSeedSpec(spec)
                ? "migration_run_toast_seed"
                : "migration_run_toast",
              {
                title,
                before: runCount(run.countsBefore, measured) ?? "?",
                after: runCount(run.countsAfter, measured) ?? "?",
                expected: spec.expectedDocuments?.count
              }
            )
          )
        } else if (run.status === "INTERRUPTED") {
          showWarningToast(translate("migration_interrupted_toast", { title }))
        } else {
          showErrorToast(run.error || translate("migration_failed_toast"))
        }
      }
      void queryClient.invalidateQueries({ queryKey: probeKey(type) })
      void queryClient.invalidateQueries({
        queryKey: ["migration-history", type]
      })
    })
    previouslyRunning.current = running
  }, [runsQuery.data, queryClient, translate, currentLanguage])

  const handleStart = async (state: MigrationState) => {
    setStarting(true)
    try {
      const response = await startMigration(state.spec.type, {
        collection: state.spec.collection,
        counts: [progressCount(state.spec)]
      })
      if (!response.data?.success || !response.data.data) {
        showErrorToast(
          response.data?.message || translate("migration_failed_toast")
        )
      } else {
        showSuccessToast(
          translate("migration_started_toast", {
            title: pickText(state.spec.title, currentLanguage)
          })
        )
      }
      setConfirming(null)
      await runsQuery.refetch()
    } finally {
      setStarting(false)
    }
  }

  const reprobe = (type: string) =>
    queryClient.refetchQueries({ queryKey: probeKey(type) })

  const reprobeAll = () =>
    Promise.all([
      queryClient.refetchQueries({ queryKey: ["migration-probe"] }),
      runsQuery.refetch()
    ])

  const [lastOpenedType, setLastOpenedType] = useState<string | null>(null)
  const openRow = (row: MigrationRow) => {
    setSelectedType(row.spec.type)
    setLastOpenedType(row.spec.type)
  }

  const summary: {
    key: string
    count: number
    color: "info" | "warning" | "success" | "default" | "error"
  }[] = [
    {
      key: "migration_summary_running",
      count: runningRows.length,
      color: "info"
    },
    {
      key: "migration_summary_attention",
      count: attentionRows.length,
      color: "warning"
    },
    {
      key: "migration_summary_applied",
      count: appliedRows.length,
      color: "success"
    },
    {
      key: "migration_summary_checking",
      count: checkingCount,
      color: "default"
    },
    { key: "migration_summary_failed", count: failedCount, color: "error" }
  ]

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="h5">
          {translate("migration_page_title")}
        </Typography>
        {probing && <CircularProgress size={18} />}
        <Box flex={1} />
        <Button variant="outlined" onClick={reprobeAll} disabled={probing}>
          {translate("migration_reprobe_all")}
        </Button>
      </Stack>

      <Stack
        direction="row"
        spacing={1}
        flexWrap="wrap"
        sx={{ mb: 2 }}
        useFlexGap
      >
        {summary
          .filter((item) => item.count > 0)
          .map((item) => (
            <Chip
              key={item.key}
              size="small"
              color={item.color}
              variant="outlined"
              label={translate(item.key, { count: item.count })}
            />
          ))}
      </Stack>

      <ActiveRunBanner rows={runningRows} now={now} onOpen={openRow} />

      {failedCount > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {translate("migration_probe_failed_banner", { count: failedCount })}
        </Alert>
      )}

      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        {translate("migration_section_attention")}
      </Typography>
      {attentionRows.length > 0 ? (
        <MigrationTable
          rows={attentionRows}
          now={now}
          onOpen={openRow}
          highlightedType={lastOpenedType}
        />
      ) : (
        <Alert severity="success">
          {translate("migration_attention_empty")}
        </Alert>
      )}

      <Accordion
        expanded={showApplied}
        onChange={(_, expanded) => setShowApplied(expanded)}
        disableGutters
        variant="outlined"
        sx={{ mt: 3 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">
            {translate("migration_section_applied", {
              count: appliedRows.length
            })}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {appliedRows.length > 0 ? (
            <MigrationTable
              rows={appliedRows}
              now={now}
              onOpen={openRow}
              highlightedType={lastOpenedType}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              {translate("migration_empty")}
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={showArchived}
        onChange={(_, expanded) => setShowArchived(expanded)}
        disableGutters
        variant="outlined"
        sx={{ mt: 2 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">
            {translate("migration_section_archive", {
              count: ARCHIVED_MIGRATIONS.length
            })}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {translate("migration_section_archive_help")}
          </Typography>
          <MigrationTable
            rows={archivedRows}
            now={now}
            onOpen={openRow}
            highlightedType={lastOpenedType}
          />
        </AccordionDetails>
      </Accordion>

      <Alert severity="info" sx={{ mt: 3 }}>
        {translate("migration_source_note")}
      </Alert>

      {selected?.state && (
        <MigrationDetailDrawer
          state={selected.state}
          lastRun={selected.lastRun}
          otherRun={runningRows.find(
            (row) => row.spec.type !== selected.spec.type
          )}
          now={now}
          onClose={() => setSelectedType(null)}
          onRun={setConfirming}
          onReprobe={() => reprobe(selected.spec.type)}
          reprobing={selected.probing}
        />
      )}

      <RunConfirmDialog
        state={confirming}
        starting={starting}
        onCancel={() => setConfirming(null)}
        onConfirm={handleStart}
      />
    </Box>
  )
}
